/** Console API client: per-environment base URL, token storage isolated per environment, automatic refresh. */

export type EnvName = 'local' | 'staging' | 'production';

export const DEFAULT_BASE_URLS: Record<EnvName, string> = {
  local: 'http://localhost:8787',
  staging: 'https://api-staging.pockettavern.app',
  production: 'https://api.pockettavern.app',
};

const ENV_KEY = 'pt.console.env';
const BASE_KEY = (env: EnvName) => `pt.console.base.${env}`;
const TOKENS_KEY = (env: EnvName) => `pt.console.tokens.${env}`;
const LOCALE_KEY = 'pt.console.locale';

/** add-tag-taxonomy: every request carries `Accept-Language` (BCP 47); the server localises tag / category names with it. */
export const CONSOLE_LOCALES = ['en', 'zh-CN', 'zh-TW', 'ja', 'ko'] as const;
export type ConsoleLocale = (typeof CONSOLE_LOCALES)[number];
export function getLocale(): ConsoleLocale {
  const saved = localStorage.getItem(LOCALE_KEY);
  if (saved && (CONSOLE_LOCALES as readonly string[]).includes(saved)) return saved as ConsoleLocale;
  const nav = navigator.language || 'en';
  const exact = CONSOLE_LOCALES.find((l) => l.toLowerCase() === nav.toLowerCase());
  if (exact) return exact;
  const lower = nav.toLowerCase();
  const primary = lower.split('-')[0] ?? 'en';
  if (primary === 'zh') return /tw|hk|hant/.test(lower) ? 'zh-TW' : 'zh-CN';
  return CONSOLE_LOCALES.find((l) => l.split('-')[0] === primary) ?? 'en';
}
export function setLocale(locale: ConsoleLocale): void {
  localStorage.setItem(LOCALE_KEY, locale);
}

export interface Tokens {
  access_token: string;
  refresh_token: string;
  expires_in: number;
  session_id: string;
}

export interface ApiError {
  code: string;
  message: string;
  [k: string]: unknown;
}

export type Envelope<T> = { success: true; data: T; meta?: unknown } | { success: false; error: ApiError };

export class ApiFailure extends Error {
  constructor(
    readonly status: number,
    readonly error: ApiError,
    readonly requestId: string | null,
  ) {
    super(`${error.code}: ${error.message}`);
  }
}

export function getEnv(): EnvName {
  const v = localStorage.getItem(ENV_KEY) as EnvName | null;
  return v && v in DEFAULT_BASE_URLS ? v : 'staging';
}
export function setEnv(env: EnvName): void {
  localStorage.setItem(ENV_KEY, env);
}
export function getBaseUrl(env: EnvName = getEnv()): string {
  return localStorage.getItem(BASE_KEY(env)) ?? DEFAULT_BASE_URLS[env];
}
export function setBaseUrl(env: EnvName, url: string): void {
  localStorage.setItem(BASE_KEY(env), url.replace(/\/+$/, ''));
}
export function getTokens(env: EnvName = getEnv()): Tokens | null {
  const raw = localStorage.getItem(TOKENS_KEY(env));
  return raw ? (JSON.parse(raw) as Tokens) : null;
}
export function setTokens(tokens: Tokens | null, env: EnvName = getEnv()): void {
  if (tokens) localStorage.setItem(TOKENS_KEY(env), JSON.stringify(tokens));
  else localStorage.removeItem(TOKENS_KEY(env));
}

let refreshing: Promise<Tokens | null> | null = null;

async function refreshTokens(): Promise<Tokens | null> {
  const current = getTokens();
  if (!current) return null;
  if (!refreshing) {
    refreshing = (async () => {
      try {
        const res = await fetch(`${getBaseUrl()}/api/auth/refresh`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ refresh_token: current.refresh_token }),
        });
        const body = (await res.json()) as Envelope<Tokens>;
        if (!body.success) {
          // AUTH_REFRESH_REUSED / AUTH_SESSION_REVOKED → force logout
          setTokens(null);
          return null;
        }
        setTokens(body.data);
        return body.data;
      } finally {
        refreshing = null;
      }
    })();
  }
  return refreshing;
}

/** change 12: after a purchase the access token's `tier` claim is stale — refresh proactively instead of eating a 401. */
export async function forceRefreshTokens(): Promise<boolean> {
  return (await refreshTokens()) !== null;
}

export interface RequestOptions {
  method?: string;
  body?: unknown;
  auth?: boolean;
  headers?: Record<string, string>;
}

export interface ApiResult<T> {
  data: T;
  status: number;
  headers: Headers;
}

/** Calls the gateway; on 401 AUTH_TOKEN_EXPIRED it refreshes once and retries. */
export async function api<T>(path: string, options: RequestOptions = {}): Promise<ApiResult<T>> {
  const { method = 'GET', body, auth = true } = options;
  const headers: Record<string, string> = { Accept: 'application/json', ...(options.headers ?? {}) };
  if (body !== undefined) headers['Content-Type'] = 'application/json';
  const res = await apiRaw(path, { method, headers, body: body === undefined ? undefined : JSON.stringify(body), auth });
  const envelope = (await res.json().catch(() => null)) as Envelope<T> | null;
  if (!envelope) throw new ApiFailure(res.status, { code: 'CONSOLE_BAD_RESPONSE', message: `Non-JSON response (${res.status})` }, res.headers.get('X-Request-Id'));
  if (!envelope.success) throw new ApiFailure(res.status, envelope.error, res.headers.get('X-Request-Id'));
  return { data: envelope.data, status: res.status, headers: res.headers };
}

export interface RawOptions {
  method?: string;
  body?: BodyInit;
  headers?: Record<string, string>;
  auth?: boolean;
}

/** Raw fetch with bearer auth and one refresh-and-retry on AUTH_TOKEN_EXPIRED (binary uploads/downloads). */
export async function apiRaw(path: string, options: RawOptions = {}): Promise<Response> {
  const { method = 'GET', body, auth = true } = options;
  const attempt = async (): Promise<Response> => {
    const headers: Record<string, string> = { 'Accept-Language': getLocale(), ...(options.headers ?? {}) };
    const tokens = getTokens();
    if (auth && tokens) headers.Authorization = `Bearer ${tokens.access_token}`;
    return fetch(`${getBaseUrl()}${path}`, { method, headers, body });
  };
  let res = await attempt();
  if (res.status === 401) {
    const peek = (await res.clone().json().catch(() => null)) as Envelope<unknown> | null;
    if (peek && !peek.success && peek.error.code === 'AUTH_TOKEN_EXPIRED' && (await refreshTokens())) res = await attempt();
  }
  return res;
}

/** Downloads a binary/JSON endpoint as a file (uses Content-Disposition filename when present). */
export async function apiDownload(path: string, fallbackName: string): Promise<void> {
  const res = await apiRaw(path);
  if (!res.ok) {
    const envelope = (await res.json().catch(() => null)) as Envelope<unknown> | null;
    throw new ApiFailure(res.status, envelope && !envelope.success ? envelope.error : { code: 'CONSOLE_DOWNLOAD_FAILED', message: `HTTP ${res.status}` }, res.headers.get('X-Request-Id'));
  }
  const disposition = res.headers.get('Content-Disposition') ?? '';
  const m = /filename\*=UTF-8''([^;]+)/.exec(disposition);
  const name = m ? decodeURIComponent(m[1]!) : fallbackName;
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export function describeError(err: unknown): string {
  if (err instanceof ApiFailure) {
    const extra = Object.entries(err.error).filter(([k]) => k !== 'code' && k !== 'message');
    return `${err.status} ${err.error.code} — ${err.error.message}${extra.length ? ' ' + JSON.stringify(Object.fromEntries(extra)).slice(0, 400) : ''}`;
  }
  return err instanceof Error ? err.message : String(err);
}
